import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Calendar, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { BeforeAfterSlider } from './BeforeAfterSlider';
interface ResultsStepProps {
  imageSrc: string;
  onRetake: () => void;
}
export function ResultsStep({
  imageSrc,
  onRetake
}: ResultsStepProps) {
  const {
    t
  } = useLanguage();
  const recommendations = [{
    id: 'cheeks',
    title: t('faceAnalysis.results.cheeks'),
    desc: t('faceAnalysis.results.cheeksDesc'),
    product: 'Singderm® Silk'
  }, {
    id: 'nasolabial',
    title: t('faceAnalysis.results.nasolabial'),
    desc: t('faceAnalysis.results.nasolabialDesc'),
    product: 'Singderm®'
  }, {
    id: 'lips',
    title: t('faceAnalysis.results.lips'),
    desc: t('faceAnalysis.results.lipsDesc'),
    product: 'Singderm® Silk'
  }, {
    id: 'jawline',
    title: t('faceAnalysis.results.jawline'),
    desc: t('faceAnalysis.results.jawlineDesc'),
    product: 'Singderm®'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-5xl mx-auto" data-id="element-267">
      <div className="text-center mb-10" data-id="element-268">
        <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4" data-id="element-269">
          {t('faceAnalysis.results.title')}
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto" data-id="element-270">
          {t('faceAnalysis.results.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-271">
        {/* Left: Slider */}
        <div data-id="element-272">
          <BeforeAfterSlider imageSrc={imageSrc} data-id="element-273" />

          <div className="mt-6 flex justify-center space-x-4" data-id="element-274">
            <button onClick={onRetake} className="flex items-center px-6 py-3 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-275">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-276" />
              {t('faceAnalysis.results.retake')}
            </button>
            <Link to="/contact" className="flex items-center px-6 py-3 rounded-full bg-brand-copper text-white hover:bg-brand-darkCopper transition-colors font-medium shadow-lg shadow-brand-copper/20" data-id="element-277">
              <Calendar className="w-4 h-4 mr-2" data-id="element-278" />
              {t('faceAnalysis.results.bookConsultation')}
            </Link>
          </div>
        </div>

        {/* Right: Recommendations */}
        <div data-id="element-279">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center" data-id="element-280">
            <CheckCircle2 className="w-5 h-5 mr-2 text-brand-copper" data-id="element-281" />
            {t('faceAnalysis.results.recommendations')}
          </h3>

          <div className="space-y-4" data-id="element-282">
            {recommendations.map((rec, idx) => <motion.div key={rec.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-283">
                <div className="flex justify-between items-start mb-2" data-id="element-284">
                  <h4 className="font-semibold text-gray-900" data-id="element-285">{rec.title}</h4>
                  <span className="text-xs font-medium bg-brand-copper/10 text-brand-darkCopper px-2 py-1 rounded-md" data-id="element-286">
                    {rec.product}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-287">
                  {rec.desc}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-8 bg-orange-50 border border-orange-100 rounded-xl p-4 flex items-start" data-id="element-288">
            <ShieldAlert className="w-5 h-5 text-orange-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-289" />
            <p className="text-xs text-orange-800 leading-relaxed" data-id="element-290">
              {t('faceAnalysis.results.disclaimer')}
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}